import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import cx from "classnames";

import styles from "./folderTabs.module.scss";
import { ScrollableContainer } from "src/components/scroller";
import { CollectionCard, PlaceholderCollectionCard } from "../collectionCard";
import useMeasure from "react-use-measure";
import { RootState } from "src/redux/store";
import _ from "lodash";
import { fetchHubSection } from "src/redux/actions/hub";
import { useDispatch } from "react-redux";
import userRoleService from "src/utils/userRoles.service";

interface HubSection {
  id: string;
  title?: string;
  items?: any[];
  [key: string]: any;
}

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.left)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.right)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

export default function FolderTabsRow({ sections }: { sections: any }) {
  const dispatch = useDispatch();
  const [carouselRef, carouselBounds] = useMeasure();

  const canAccess = userRoleService.canAccessCollection(); // Check if user has access to collections
  const [activeSectionId, setActiveSectionId] = React.useState<string | null>(null);
  const sectionItems = useSelector(
    (state: RootState) => state.hub?.sections,
  );
  const [activeSectionItems, setActiveSectionItems] = React.useState<any[]>([]);
  const sectionIds = sections.map((section: HubSection) => section.section.id);

  useEffect(() => {
    if(_.size(sections) > 0){
      sections.forEach((section: HubSection) => { // Fetch items for all sections
        dispatch(fetchHubSection(section.section.id));
      });
    }
  }, [sections]);

  const groupSectionItems = () => { // chuck the section items.
      if(activeSectionId === null) return;

      const cardWidth = 240; // fixed width of content
      const cardGap = 9; // gap
      
      const activeSectionItems = sectionItems[activeSectionId]?.items || [];
      if (!carouselBounds.width || _.size(activeSectionItems) === 0) return;

      // parentWidth & contentWidth
      const parentWidth = carouselBounds.width - 10;
      const contentWidth = cardWidth + cardGap;

      const perRowFits = Math.floor(parentWidth/contentWidth); // find the how many items can fit in it 
      const perSlide = perRowFits * 2; // * two rows

      setActiveSectionItems(_.chunk(activeSectionItems, perSlide));
  }

  const emptySectionIds = _.filter(sectionIds, id => { // filter out empty sections id's
    const items = _.get(sectionItems, `${id}.items`, []);
    return _.size(items) === 0;
  })

  const allSectionsLoaded = _.every(sectionIds, // check if all sections are loaded
    id => _.has(sectionItems, id)
  );
  useEffect(() => {
     groupSectionItems();
  }, [carouselBounds.width, activeSectionId, sectionItems]);


  useEffect(() => {  // Initial section selection 
    if(!allSectionsLoaded || anyLoading || activeSectionId) return;

    const initSelectedSection = sections.find((col: HubSection) => {
      const items = _.get(sectionItems, `${col.section.id}.items`, []);
      return _.size(items) > 0;
    });

    if(initSelectedSection){
      setActiveSectionId(initSelectedSection.section.id)
    }
  }, [sectionItems])

  if (!canAccess) return null;

  if(_.size(sectionItems) === 0) { // if sections not loaded yet
    return <PlaceHolderFolderTabsRow /> ;
  }

  if (!allSectionsLoaded) {
    return <PlaceHolderFolderTabsRow />
  }

  const anyLoading = _.some(sectionIds, // if sections are loaded, check if any section is still loading
    id => _.get(sectionItems, `${id}.loading`) === true
  );

  if (anyLoading) {
    return <PlaceHolderFolderTabsRow />
  }

  if(_.size(emptySectionIds) === _.size(sectionIds)) {
    return null; // all sections are empty
  }

  return (
    <div className={styles.folderTabsSection}>
      <div className={styles.headerRow}>
        <ScrollableContainer 
          distance={100}
          wrapperClassName={styles.tabsWrapperContainer} 
         >
            <div className={styles.tabs}>  
                { _.map(sections, (section: any) => {
                  return !_.includes(emptySectionIds, section.section.id) && (
                  <button
                    key={section.section.id}
                    className={cx(styles.tab, {
                      [styles.active]: section.section.id === activeSectionId,
                    })}
                    onClick={() => setActiveSectionId(section.section.id)}
                  >
                {section.section.title}
               </button>) })}
            </div>
        </ScrollableContainer>
      </div>

    {/* Contain the folder content items */}
    <ScrollableContainer
      distance={carouselBounds.width}
      leftArrowComponent={LeftArrow}
      rightArrowComponent={RightArrow}
      key={activeSectionId}
      wrapperClassName={styles.wrapperContainer}
    >
             <div ref={carouselRef} className={styles.contentArea}>
              {activeSectionItems.map((groupItem, groupIndex) => (
                <div className={styles.groupCard} key={groupIndex}>
                    {groupItem.map((item: any) => (
                        <CollectionCard key={item.id} {...item} classnames={styles.card} />
                    ))}
                </div> ))}
             </div>
      </ScrollableContainer>
    </div>
  )
}

const PlaceHolderFolderTabsRow = () => {
  return (
    <div className={styles.placeholderFolderTabsRow}>
      {_.times(10).map((i) => <PlaceholderCollectionCard key={i} />)}
    </div>
  )
}